import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping } from '../../../Clients/ImportClients/autoMap';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { autoMapAppointments } from './autoMapAppointments';
import { applyStoredAppointmentMapping } from './useAppointmentMappingStorage';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';
import UploadStep from './UploadStep';
import AppointmentMappingStep from './AppointmentMappingStep';
import ProfessionalMappingStep, { areProfessionalsMapped, getUniqueProfessionalNames } from './ProfessionalMappingStep';
import ServiceMappingStep from './ServiceMappingStep';
import AppointmentPreviewStep from './AppointmentPreviewStep';
import AppointmentImportStep from './AppointmentImportStep';

type ModalStep = 'upload' | 'map' | 'mapProfessionals' | 'mapServices' | 'preview' | 'import';

interface StepDef {
    id: ModalStep;
    labelKey: TK;
}

const STEPS: StepDef[] = [
    { id: 'upload',           labelKey: TK.importAppointmentsStepUpload           },
    { id: 'map',              labelKey: TK.importAppointmentsStepMap              },
    { id: 'mapProfessionals', labelKey: TK.importAppointmentsStepMapProfessionals },
    { id: 'mapServices',      labelKey: TK.importAppointmentsStepMapServices      },
    { id: 'preview',          labelKey: TK.importAppointmentsStepPreview          },
    { id: 'import',           labelKey: TK.importAppointmentsStepImport           },
];

const REQUIRED_FIELDS = ['date', 'professional', 'client'] as const;

function buildDefaultOptions(generalSettings: ReturnType<typeof getGeneralSettings>): ImportAppointmentOptions {
    const roomKeys = Object.keys(generalSettings?.rooms ?? {});
    const defaultRoom = !generalSettings?.allowAppointmentsWithoutRoom && roomKeys.length > 0
        ? roomKeys[0]
        : undefined;
    return {
        createMissingClients: true,
        defaultRoom,
        defaultDurationMinutes: 30,
        professionalMappings: {},
        serviceMappings: {},
    };
}

interface ImportAppointmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ImportAppointmentsModal: React.FC<ImportAppointmentsModalProps> = ({ isOpen, onClose }) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);

    const [step, setStep] = React.useState<ModalStep>('upload');
    const [analysisResult, setAnalysisResult] = React.useState<AnalysisResult | null>(null);
    const [mapping, setMapping] = React.useState<ColumnMapping | null>(null);
    const [options, setOptions] = React.useState<ImportAppointmentOptions>(() => buildDefaultOptions(generalSettings));

    React.useEffect(() => {
        if (!isOpen) {
            setStep('upload');
            setAnalysisResult(null);
            setMapping(null);
            setOptions(buildDefaultOptions(generalSettings));
        }
    }, [isOpen]);

    const currentStepIndex = STEPS.findIndex(s => s.id === step);

    const goToStep = React.useCallback((target: ModalStep) => {
        if (target === 'upload') {
            setAnalysisResult(null);
            setMapping(null);
        }
        setStep(target);
    }, []);

    const handleAnalysisComplete = React.useCallback((result: AnalysisResult) => {
        setAnalysisResult(result);
        const initial = autoMapAppointments(result);
        setMapping(applyStoredAppointmentMapping(initial, result.headers));
        setStep('map');
    }, []);

    const isMappingValid = React.useMemo(
        () => mapping !== null && REQUIRED_FIELDS.every(f => (mapping.fieldMap[f]?.length ?? 0) > 0),
        [mapping],
    );

    const profColIndex = mapping?.fieldMap['professional']?.[0] ?? -1;
    const typeColIndex = mapping?.fieldMap['type']?.[0] ?? -1;

    const uniqueProfNames = React.useMemo(
        () => analysisResult ? getUniqueProfessionalNames(analysisResult.allRows, profColIndex) : [],
        [analysisResult, profColIndex],
    );
    const isProfMappingValid = React.useMemo(
        () => areProfessionalsMapped(uniqueProfNames, options.professionalMappings),
        [uniqueProfNames, options.professionalMappings],
    );

    // Determine which steps are present based on what columns are mapped
    const hasProfStep = profColIndex !== -1;
    const hasServiceStep = typeColIndex !== -1;

    // Advance from column-mapping: skip professional step if column not mapped,
    // skip service step if type column not mapped
    const handleColumnMappingNext = React.useCallback(() => {
        if (hasProfStep) setStep('mapProfessionals');
        else if (hasServiceStep) setStep('mapServices');
        else setStep('preview');
    }, [hasProfStep, hasServiceStep]);

    // Advance from professional-mapping: skip service step if type column not mapped
    const handleProfMappingNext = React.useCallback(() => {
        setStep(hasServiceStep ? 'mapServices' : 'preview');
    }, [hasServiceStep]);

    // Back from service-mapping: return to professional-mapping or column-mapping
    const handleServiceMappingBack = React.useCallback(() => {
        setStep(hasProfStep ? 'mapProfessionals' : 'map');
    }, [hasProfStep]);

    const stepIndicator = (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacer-M)',
            paddingBottom: 'var(--spacer-M)',
            borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
        }}>
            {STEPS.map((s, i) => {
                const isPast = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const circleColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)';
                const labelColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--text-muted)';

                return (
                    <React.Fragment key={s.id}>
                        {i > 0 && (
                            <div style={{
                                flex: 1,
                                height: '2px',
                                marginTop: '13px',
                                background: isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)',
                                transition: 'background 0.2s',
                            }} />
                        )}
                        <div
                            onClick={isPast && step !== 'import' ? () => goToStep(s.id) : undefined}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--spacer-XXS)',
                                cursor: isPast && step !== 'import' ? 'pointer' : 'default',
                                minWidth: '64px',
                            }}
                        >
                            <div style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                background: circleColor,
                                color: isCurrent || isPast ? 'white' : 'var(--text-muted)',
                                transition: 'background 0.2s',
                                flexShrink: 0,
                            }}>
                                {i + 1}
                            </div>
                            <span style={{
                                fontSize: '0.72rem',
                                color: labelColor,
                                fontWeight: isCurrent ? 600 : 400,
                                whiteSpace: 'nowrap',
                                transition: 'color 0.2s',
                                textAlign: 'center',
                            }}>
                                {t(s.labelKey)}
                            </span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl">
            <ModalHeader toggle={onClose}>{t(TK.importAppointments)}</ModalHeader>
            <ModalBody>
                {stepIndicator}
                {step === 'upload' && (
                    <UploadStep onComplete={handleAnalysisComplete} />
                )}
                {step === 'map' && analysisResult && mapping && (
                    <AppointmentMappingStep
                        result={analysisResult}
                        mapping={mapping}
                        onMappingChange={setMapping}
                        options={options}
                        onOptionsChange={setOptions}
                    />
                )}
                {step === 'mapProfessionals' && analysisResult && mapping && (
                    <ProfessionalMappingStep
                        result={analysisResult}
                        mapping={mapping}
                        options={options}
                        onOptionsChange={setOptions}
                    />
                )}
                {step === 'mapServices' && analysisResult && mapping && (
                    <ServiceMappingStep
                        result={analysisResult}
                        mapping={mapping}
                        options={options}
                        onOptionsChange={setOptions}
                    />
                )}
                {step === 'preview' && analysisResult && mapping && (
                    <AppointmentPreviewStep
                        result={analysisResult}
                        mapping={mapping}
                        options={options}
                    />
                )}
                {step === 'import' && analysisResult && mapping && (
                    <AppointmentImportStep
                        result={analysisResult}
                        mapping={mapping}
                        options={options}
                        onClose={onClose}
                    />
                )}
            </ModalBody>
            {step === 'map' && (
                <ModalFooter>
                    {!isMappingValid && (
                        <span style={{ marginRight: 'auto', color: 'var(--danger-color)', fontSize: '0.85rem' }}>
                            {t(TK.importAppointmentsMappingRequired)}
                        </span>
                    )}
                    <Button color="secondary" onClick={() => goToStep('upload')}>
                        {t(TK.back)}
                    </Button>
                    <Button color="success" disabled={!isMappingValid} onClick={handleColumnMappingNext}>
                        {t(TK.next)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'mapProfessionals' && (
                <ModalFooter>
                    {!isProfMappingValid && (
                        <span style={{ marginRight: 'auto', color: 'var(--danger-color)', fontSize: '0.85rem' }}>
                            {t(TK.importAppointmentsProfessionalMappingRequired)}
                        </span>
                    )}
                    <Button color="secondary" onClick={() => setStep('map')}>
                        {t(TK.back)}
                    </Button>
                    <Button color="success" disabled={!isProfMappingValid} onClick={handleProfMappingNext}>
                        {t(TK.next)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'mapServices' && (
                <ModalFooter>
                    <Button color="secondary" onClick={handleServiceMappingBack}>
                        {t(TK.back)}
                    </Button>
                    <Button color="success" onClick={() => setStep('preview')}>
                        {t(TK.next)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'preview' && (
                <ModalFooter>
                    <Button color="secondary" onClick={() => setStep(hasServiceStep ? 'mapServices' : hasProfStep ? 'mapProfessionals' : 'map')}>
                        {t(TK.back)}
                    </Button>
                    <Button color="success" onClick={() => setStep('import')}>
                        {t(TK.importAppointmentsStartImport)}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
};

export default ImportAppointmentsModal;
